import React, { useEffect, useRef, useState } from "react";
import PixelGlyph from "./PixelGlyph";
import { useVolume } from "./VolumeContext";
import { beginDrag, clamp } from "../utils/desktop";

const KEY_STEP = 0.05;
// Keeps the thumb's center inside the track at both ends.
const THUMB_HALF = 6;

const CHECK = [
  "......#",
  ".....##",
  "#...###",
  "##.###.",
  "#####..",
  ".###...",
  "..#....",
];

const VolumeSlider = ({ level, onChange }) => {
  const trackRef = useRef(null);

  const levelAt = (clientY) => {
    const b = trackRef.current.getBoundingClientRect();
    return clamp(1 - (clientY - b.top) / b.height, 0, 1);
  };

  const handlePointerDown = (e) => {
    const y0 = e.clientY;
    onChange(levelAt(y0));
    beginDrag(e, (dx, dy) => onChange(levelAt(y0 + dy)), () => {});
  };

  const handleKeyDown = (e) => {
    const next = {
      ArrowUp: level + KEY_STEP,
      ArrowRight: level + KEY_STEP,
      ArrowDown: level - KEY_STEP,
      ArrowLeft: level - KEY_STEP,
      Home: 0,
      End: 1,
    }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    onChange(next);
  };

  return (
    <div
      role="slider"
      aria-label="Volume"
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(level * 100)}
      tabIndex={0}
      className="relative w-9 h-28 cursor-pointer touch-none outline-none focus-visible:outline-dotted focus-visible:outline-1 focus-visible:outline-black"
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
    >
      {/* Tick marks */}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <div
          key={t}
          className="absolute left-0 w-[5px] h-px bg-black"
          style={{ top: `calc(${THUMB_HALF}px + ${t} * (100% - ${2 * THUMB_HALF}px))` }}
        />
      ))}
      <div
        ref={trackRef}
        className="absolute left-1/2 -translate-x-1/2 w-[4px] bg-black border border-t-[#808080] border-l-[#808080] border-b-white border-r-white"
        style={{ top: THUMB_HALF, bottom: THUMB_HALF }}
      />
      <div
        className="absolute left-1/2 -translate-x-1/2 w-[22px] bg-[#c0c0c0] border-2 border-t-white border-l-white border-b-black border-r-black"
        style={{
          height: 2 * THUMB_HALF,
          top: `calc(${1 - level} * (100% - ${2 * THUMB_HALF}px))`,
        }}
      />
    </div>
  );
};

const VolumeControl = () => {
  const { level, muted, setLevel, setMuted } = useVolume();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const silent = muted || level === 0;

  return (
    <div ref={rootRef} className="relative flex">
      <button
        className="relative h-6 w-6"
        aria-label={silent ? "Volume (muted)" : "Volume"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <img
          src={silent ? "/loudspeaker-muted-icon.png" : "/loudspeaker-icon.png"}
          alt=""
          className="h-6 w-6"
          draggable={false}
        />
      </button>

      {open && (
        <div className="absolute bottom-[calc(100%+0.9rem)] left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 px-3 pt-2 pb-2 bg-[#c0c0c0] border-2 border-t-white border-l-white border-b-black border-r-black desktop-font text-2xl text-black select-none">
          <span className="leading-none mt-[0.2rem]">Volume</span>
          <VolumeSlider level={level} onChange={setLevel} />
          <label className="flex items-center gap-[0.35rem] cursor-pointer">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={muted}
              onChange={(e) => setMuted(e.target.checked)}
            />
            <span className="flex items-center justify-center w-[15px] h-[15px] bg-white border-2 border-t-[#808080] border-l-[#808080] border-b-white border-r-white peer-focus-visible:outline-dotted peer-focus-visible:outline-1 peer-focus-visible:outline-black">
              {muted && <PixelGlyph rows={CHECK} />}
            </span>
            <span className="leading-none mt-[0.2rem]">Mute</span>
          </label>
        </div>
      )}
    </div>
  );
};

export default VolumeControl;
